import JSZip from 'jszip'
import { taggedTimeline } from './timeline.js'

// Builders for everything the export panel produces: CSV, ffmpeg filter
// script + render scripts, project JSON validation, and ZIP assembly.
// All functions are pure except buildZip (JSZip).

export function buildCsv(moves, stamps) {
  const lines = ['index,ply,move,san,seconds']
  moves.forEach((move, i) => {
    const stamp = stamps[i]
    const seconds = stamp == null ? '' : stamp.toFixed(3)
    lines.push(`${i + 1},${move.ply},${move.label},${move.san},${seconds}`)
  })
  return lines.join('\n') + '\n'
}

// One overlay window per tagged move: [tₖ, tₖ₊₁), the last running to the end
// of the video. Optional lead-in with the starting position (index -1).
export function buildSegments(stamps, duration, showBefore) {
  const timeline = taggedTimeline(stamps)
  if (!timeline.length) return []
  const last = timeline[timeline.length - 1]
  const end = Number.isFinite(duration) && duration > last.time ? duration : last.time + 600
  const segments = []
  if (showBefore && timeline[0].time > 0) {
    segments.push({ index: -1, start: 0, end: timeline[0].time })
  }
  timeline.forEach((entry, i) => {
    const next = i + 1 < timeline.length ? timeline[i + 1].time : end
    segments.push({ index: entry.index, start: entry.time, end: next })
  })
  return segments.filter((s) => s.end > s.start)
}

// Pixel geometry of the board on the real video frame. Mirrors the preview
// overlay's fraction math (content box == the full frame when burning in).
// Board size is forced even so yuv420p subsampling stays happy.
export function exportGeometry({ overlay, videoWidth, videoHeight }) {
  let size = Math.round(overlay.size * videoHeight)
  size -= size % 2
  const x = overlay.customized
    ? Math.round(overlay.x * videoWidth)
    : Math.round((videoWidth - size) / 2)
  const y = Math.round(overlay.y * videoHeight)
  return { x, y, size }
}

const fmtNum = (n) => String(Math.round(n * 1000) / 1000)

export function segName(i) {
  return `seg_${String(i).padStart(3, '0')}.png`
}

// [0:v][1:v]overlay=x:y:enable='between(t,a,b)'[v1];[v1][2:v]…[out]
// Board PNGs are plain single-frame inputs: they hit EOF immediately and
// overlay's default eof_action=repeat holds the frame, while `enable`
// controls when each one is actually composited.
export function buildFilterScript(segments, { x, y }) {
  return segments
    .map((seg, i) => {
      const src = i === 0 ? '[0:v]' : `[v${i}]`
      const outLabel = i === segments.length - 1 ? '[out]' : `[v${i + 1}]`
      const enable = `enable='between(t,${fmtNum(seg.start)},${fmtNum(seg.end)})'`
      return `${src}[${i + 1}:v]overlay=${x}:${y}:${enable}${outLabel}`
    })
    .join(';')
}

export function buildRenderScripts({ videoFileName, segmentCount, outputName = 'with-board.mp4' }) {
  const inputs = Array.from({ length: segmentCount }, (_, i) => `-i "boards/${segName(i)}"`).join(' ')
  const ffmpegArgs =
    `${inputs} -filter_complex_script "filter.txt" -map "[out]" -map "0:a?" -c:a copy ` +
    `-c:v libx264 -crf 18 -preset medium -pix_fmt yuv420p "${outputName}"`

  const ps1 = `# PGN Sync render kit — Windows (PowerShell)
# Put this folder next to your video, then run:  .\\render.ps1
# Requires ffmpeg on PATH (https://ffmpeg.org/download.html).

$video = "${videoFileName}"
if (!(Test-Path $video)) {
  Write-Host "Video not found: $video — copy it next to this script (or edit \`$video above)." -ForegroundColor Yellow
  exit 1
}
ffmpeg -y -i "$video" ${ffmpegArgs}
`

  const sh = `#!/bin/sh
# PGN Sync render kit — macOS / Linux
# Put this folder next to your video, then run:  sh render.sh
# Requires ffmpeg on PATH.

VIDEO="${videoFileName}"
if [ ! -f "$VIDEO" ]; then
  echo "Video not found: $VIDEO — copy it next to this script (or edit VIDEO above)." >&2
  exit 1
fi
ffmpeg -y -i "$VIDEO" ${ffmpegArgs}
`

  return { ps1, sh, ffmpegArgs }
}

export function buildKitReadme({ videoFileName, segmentCount, geometry, videoWidth, videoHeight, extra = '' }) {
  return `PGN Sync — render kit
=====================

This folder burns the board overlay into your footage with local ffmpeg.

Contents
  boards/seg_*.png   ${segmentCount} board images, one per timestamp window,
                     rendered at ${geometry.size}×${geometry.size}px for your ${videoWidth}×${videoHeight} video
  filter.txt         the ffmpeg overlay filter graph (position ${geometry.x},${geometry.y})
  render.ps1         run on Windows:      .\\render.ps1
  render.sh          run on macOS/Linux:  sh render.sh
  mapping.json       the full project (re-import it in PGN Sync any time)
  moves.csv          move ↔ seconds table for spreadsheets
${extra ? `\n${extra}\n` : ''}
Steps
  1. Install ffmpeg (https://ffmpeg.org/download.html) if you don't have it.
  2. Unzip this kit into the folder that contains "${videoFileName}".
  3. Run the script for your OS. Output: with-board.mp4 next to the video.

Tweaks
  - Quality/size: change "-crf 18" (lower = better/bigger, 17–23 is sane).
  - Speed: change "-preset medium" (slower = smaller file).
  - Different video name? Edit the variable at the top of the script.
  - The overlay geometry is baked into filter.txt and the PNG size. To move
    or restyle the board, adjust it in PGN Sync and export the kit again.
`
}

export function parseProjectJson(text) {
  let data
  try {
    data = JSON.parse(text)
  } catch {
    return { snapshot: null, error: 'That file is not valid JSON.' }
  }
  if (!data || typeof data !== 'object' || data.app !== 'pgn-sync') {
    return { snapshot: null, error: 'That file is not a PGN Sync project.' }
  }
  if (data.version !== 1) {
    return { snapshot: null, error: `Unsupported project version (${data.version}).` }
  }
  if (typeof data.pgn !== 'string') {
    return { snapshot: null, error: 'The project file has no PGN.' }
  }
  if (data.stamps !== undefined && !Array.isArray(data.stamps)) {
    return { snapshot: null, error: 'The project file has malformed timestamps.' }
  }
  return { snapshot: data, error: null }
}

// files: [{ path, content }] where content is string | Blob | Uint8Array.
export async function buildZip(files) {
  const zip = new JSZip()
  for (const { path, content } of files) {
    zip.file(path, content)
  }
  return zip.generateAsync({ type: 'blob' })
}
